import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, supabase } from '../lib/api';
import { format, addDays, startOfDay } from 'date-fns';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import PushPromptCard from '../components/PushPromptCard';
import LoginToBook from '../components/LoginToBook';
import { useAuth } from '../App';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const TREATMENT_PHOTOS = {
  rlt:              'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/RLT%20photo.jpeg',
  cryo:             'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/cryo2.jpeg',
  'hbot-30':        'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/HBOT%20photo.jpeg',
  'hbot-60':        'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/HBOT%20photo.jpeg',
  'hbot-90':        'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/HBOT%20photo.jpeg',
  'sauna-communal': 'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/iglucraft%20sauna.png',
  'sauna-private':  'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/iglucraft%20sauna.png',
  mat:              'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/Mat.jpeg',
  nmt:              'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/nmt.png',
  'nmt-assessment': 'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/nmt1st%20session.png',
};

function BookingForm({ treatment, slot, wallet, creditBalance = 0, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const price = treatment.payg_price_pence || 0;
  const canUseCredit = price > 0 && creditBalance >= price;
  const [method, setMethod]   = useState(canUseCredit ? 'credit' : (treatment.points_cost && wallet?.balance >= treatment.points_cost ? 'points' : 'card'));
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  async function handleBook() {
    setLoading(true); setError('');
    try {
      if (method === 'credit') {
        await api.createBooking({ slotId: slot.id, treatmentId: treatment.id, paymentMethod: 'credit' });
      } else if (method === 'points') {
        await api.createBooking({ slotId: slot.id, treatmentId: treatment.id, paymentMethod: 'points' });
      } else {
        const { paymentMethod, error: pmErr } = await stripe.createPaymentMethod({
          type: 'card', card: elements.getElement(CardElement),
        });
        if (pmErr) { setError(pmErr.message); setLoading(false); return; }
        const res = await api.createBooking({ slotId: slot.id, treatmentId: treatment.id, paymentMethod: 'card', stripePaymentMethodId: paymentMethod.id });

        // Bank verification needed (3D Secure). Present the challenge, then
        // finalise — the slot is only booked once the payment truly succeeds.
        if (res && res.requiresAction) {
          const { error: actionErr, paymentIntent } = await stripe.confirmCardPayment(res.clientSecret);
          if (actionErr) { setError(actionErr.message || 'We couldn\'t verify your card with your bank. Please try again.'); setLoading(false); return; }
          if (!paymentIntent || paymentIntent.status !== 'succeeded') { setError('Your payment wasn\'t completed. Please try again.'); setLoading(false); return; }
          await api.finalizeBooking({ paymentIntentId: paymentIntent.id });
        }
      }
      onSuccess();
    } catch (err) { setError(err.error || 'Booking failed. Please try again.'); }
    setLoading(false);
  }

  const canUsePoints = treatment.points_cost && wallet?.balance >= treatment.points_cost;

  return (
    <div>
      {/* Payment method toggle */}
      {treatment.points_cost && treatment.payg_price_pence && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[
            { value: 'points', label: `${treatment.points_cost} pts`, disabled: !canUsePoints },
            { value: 'card',   label: `£${(treatment.payg_price_pence / 100).toFixed(0)} card` },
          ].map(opt => (
            <button key={opt.value} onClick={() => !opt.disabled && setMethod(opt.value)} disabled={opt.disabled} style={{
              flex: 1, padding: '12px 8px', borderRadius: 10, fontWeight: 700, fontSize: '0.9rem',
              border: `2px solid ${method === opt.value ? 'var(--green)' : 'var(--grey-300)'}`,
              background: method === opt.value ? 'var(--green-pale)' : '#fff',
              color: method === opt.value ? 'var(--green)' : 'var(--grey-700)',
              cursor: opt.disabled ? 'not-allowed' : 'pointer', opacity: opt.disabled ? 0.5 : 1,
            }}>
              {opt.label}
              {opt.value === 'points' && !canUsePoints && <div style={{ fontSize: '0.65rem', fontWeight: 400, marginTop: 2 }}>Insufficient points</div>}
            </button>
          ))}
        </div>
      )}

      {/* Account credit */}
      {method === 'credit' && (
        <div style={{ background: 'var(--green-pale)', border: '1px solid #C8E6C9', borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--green)' }}>Using your account credit 🎉</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--grey-700)', marginTop: 4 }}>
            £{(price / 100).toFixed(2)} will come off your £{(creditBalance / 100).toFixed(2)} credit, leaving £{((creditBalance - price) / 100).toFixed(2)}.
          </div>
          <button type="button" onClick={() => setMethod('card')} style={{ marginTop: 8, background: 'none', border: 'none', color: '#FF6200', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', padding: 0 }}>
            Pay by card instead
          </button>
        </div>
      )}

      {method === 'card' && (
        <>
          {canUseCredit && (
            <button type="button" onClick={() => setMethod('credit')} style={{ display: 'block', marginBottom: 12, background: 'none', border: 'none', color: '#FF6200', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', padding: 0 }}>
              ← Use my £{(creditBalance / 100).toFixed(2)} account credit instead
            </button>
          )}
          <div style={{ background: 'var(--grey-100)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <CardElement options={{ style: { base: { fontSize: '16px', color: '#1A1A1A' } } }} />
          </div>
        </>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      <button className="btn btn-primary btn-full" onClick={handleBook} disabled={loading}>
        {loading ? 'Booking…' : `Confirm Booking`}
      </button>
      <p style={{ textAlign: 'center', color: 'var(--grey-500)', fontSize: '0.78rem', marginTop: 12 }}>
        {method === 'points'
          ? `${treatment.points_cost} points will be deducted from your wallet`
          : method === 'credit'
            ? `£${(price / 100).toFixed(2)} will come off your account credit`
            : `£${(treatment.payg_price_pence / 100).toFixed(2)} will be charged to your card`}
      </p>
    </div>
  );
}

// InBody 770 rescans have their own eligibility flow rather than the standard
// points/card toggle:
//   • Active gym membership  → free automatically (verified server-side).
//   • No membership on file  → asked whether they do PT at Team Breakthrough
//     ≥1x/week. Yes → free (honour system); No → £20 by card.
// The server re-checks membership and only trusts the PT answer when there's
// no membership, so this can't be gamed by a member to dodge a (non-existent)
// charge — it only ever routes to free or £20.
function InBodyBookingForm({ treatment, slot, onSuccess }) {
  const stripe   = useStripe();
  const elements = useElements();
  const price    = treatment.payg_price_pence || 2000;
  const [status, setStatus]             = useState('loading'); // 'loading' | 'ready'
  const [hasMembership, setHasMembership] = useState(false);
  const [ptAnswer, setPtAnswer]         = useState(null);      // null | 'yes' | 'no'
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');

  useEffect(() => {
    api.getTeamupMembershipStatus()
      .then(r => setHasMembership(!!r?.hasActiveMembership))
      .catch(() => setHasMembership(false))
      .finally(() => setStatus('ready'));
  }, []);

  const isFree = hasMembership || ptAnswer === 'yes';

  async function handleBook() {
    setLoading(true); setError('');
    try {
      if (isFree) {
        await api.createBooking({
          slotId: slot.id, treatmentId: treatment.id,
          paymentMethod: 'free',
          ptDeclared: !hasMembership && ptAnswer === 'yes',
        });
      } else {
        const { paymentMethod, error: pmErr } = await stripe.createPaymentMethod({
          type: 'card', card: elements.getElement(CardElement),
        });
        if (pmErr) { setError(pmErr.message); setLoading(false); return; }
        const res = await api.createBooking({
          slotId: slot.id, treatmentId: treatment.id,
          paymentMethod: 'card', stripePaymentMethodId: paymentMethod.id,
        });
        if (res && res.requiresAction) {
          const { error: actionErr, paymentIntent } = await stripe.confirmCardPayment(res.clientSecret);
          if (actionErr) { setError(actionErr.message || 'We couldn\'t verify your card with your bank. Please try again.'); setLoading(false); return; }
          if (!paymentIntent || paymentIntent.status !== 'succeeded') { setError('Your payment wasn\'t completed. Please try again.'); setLoading(false); return; }
          await api.finalizeBooking({ paymentIntentId: paymentIntent.id });
        }
      }
      onSuccess();
    } catch (err) { setError(err.error || 'Booking failed. Please try again.'); }
    setLoading(false);
  }

  if (status === 'loading') return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div>
      {hasMembership ? (
        <div style={{ background: 'var(--green-pale)', border: '1px solid #C8E6C9', borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--green)' }}>Free with your membership 🎉</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--grey-700)', marginTop: 4 }}>
            InBody rescans are included free for members — no payment needed.
          </div>
        </div>
      ) : (
        <>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 10 }}>
            Do you do personal training at Team Breakthrough once a week or more?
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[{ v: 'yes', l: 'Yes' }, { v: 'no', l: 'No' }].map(opt => (
              <button key={opt.v} type="button" onClick={() => setPtAnswer(opt.v)} style={{
                flex: 1, padding: '12px 8px', borderRadius: 10, fontWeight: 700, fontSize: '0.9rem',
                border: `2px solid ${ptAnswer === opt.v ? 'var(--green)' : 'var(--grey-300)'}`,
                background: ptAnswer === opt.v ? 'var(--green-pale)' : '#fff',
                color: ptAnswer === opt.v ? 'var(--green)' : 'var(--grey-700)', cursor: 'pointer',
              }}>{opt.l}</button>
            ))}
          </div>
          {ptAnswer === 'yes' && (
            <div style={{ background: 'var(--green-pale)', border: '1px solid #C8E6C9', borderRadius: 10, padding: 14, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--green)' }}>Free scan 🎉</div>
              <div style={{ fontSize: '0.82rem', color: 'var(--grey-700)', marginTop: 4 }}>
                PT clients get free InBody rescans — no payment needed.
              </div>
            </div>
          )}
          {ptAnswer === 'no' && (
            <>
              <div style={{ fontSize: '0.85rem', color: 'var(--grey-700)', marginBottom: 12 }}>
                A one-off InBody rescan is <strong>£{(price / 100).toFixed(0)}</strong>.
              </div>
              <div style={{ background: 'var(--grey-100)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <CardElement options={{ style: { base: { fontSize: '16px', color: '#1A1A1A' } } }} />
              </div>
            </>
          )}
        </>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      <button className="btn btn-primary btn-full" onClick={handleBook} disabled={loading || (!hasMembership && !ptAnswer)}>
        {loading ? 'Booking…' : isFree ? 'Confirm Free Booking' : `Pay £${(price / 100).toFixed(0)} & Book`}
      </button>
      <p style={{ textAlign: 'center', color: 'var(--grey-500)', fontSize: '0.78rem', marginTop: 12 }}>
        {isFree
          ? 'This scan is free — nothing will be charged.'
          : `£${(price / 100).toFixed(2)} will be charged to your card`}
      </p>
    </div>
  );
}

export default function BookTreatment() {
  const { slug }  = useParams();
  const nav       = useNavigate();
  const { session } = useAuth();
  const [treatment, setTreatment] = useState(null);
  const [wallet, setWallet]       = useState(null);
  const [date, setDate]           = useState(format(new Date(), 'yyyy-MM-dd'));
  const [slots, setSlots]         = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [success, setSuccess]     = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);

  useEffect(() => {
    // Read both from Supabase directly for reliability (same pattern as the
    // rest of the app). Avoids the flaky backend /treatments and /wallet
    // endpoints — members were seeing 0 points and missing treatments due to
    // intermittent backend failures.
    Promise.all([
      supabase
        .from('treatments')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .single()
        .then(({ data }) => data),
      supabase.rpc('get_my_wallet_data').then(({ data }) => ({
        balance: data?.balance || 0,
        transactions: data?.transactions || [],
        nextExpiry: data?.nextExpiry || null,
      })),
      // Member's £ account credit balance (for NMT rebooks after a cancellation).
      supabase.auth.getUser().then(async ({ data: u }) => {
        if (!u?.user?.id) return 0;
        const { data: p } = await supabase.from('profiles').select('credit_balance_pence').eq('id', u.user.id).single();
        return p?.credit_balance_pence || 0;
      }).catch(() => 0),
    ]).then(([tx, w, credit]) => {
      if (!tx) return nav('/wellness');
      setTreatment(tx); setWallet(w); setCreditBalance(credit || 0); setLoading(false);
    });
  }, [slug]);

  useEffect(() => {
    if (!treatment) return;
    setSlotsLoading(true);
    api.getSlots(treatment.resource_type, date).then(({ slots }) => {
      setSlots(slots); setSelectedSlot(null); setSlotsLoading(false);
    });
  }, [treatment, date]);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  if (success) return (
    <div className="page" style={{ textAlign: 'center', paddingTop: 60 }}>
      <div style={{ fontSize: '3rem', marginBottom: 16 }}>✅</div>
      <h2 style={{ fontWeight: 800, marginBottom: 8 }}>You're booked in!</h2>
      <p style={{ color: 'var(--grey-700)', marginBottom: 8 }}>{treatment.name}</p>
      <p style={{ color: 'var(--grey-700)', marginBottom: 24 }}>{format(new Date(selectedSlot.starts_at), "EEEE d MMMM 'at' h:mm aaa")}</p>
      <p style={{ color: 'var(--grey-500)', fontSize: '0.85rem', marginBottom: 24 }}>Confirmation sent to your email.</p>

      <PushPromptCard moment="booking" />

      <button className="btn btn-primary" onClick={() => nav('/wellness')}>Back to Wellness</button>
    </div>
  );

  // Date picker — next 8 weeks (56 days)
  const dates = Array.from({ length: 56 }, (_, i) => format(addDays(new Date(), i), 'yyyy-MM-dd'));

  // A treatment longer than the slot length needs back-to-back slots (e.g. the
  // 60-min NMT assessment on a 30-min diary needs two). Only offer start times
  // where the required consecutive slots are all free.
  const displaySlots = (() => {
    if (!slots.length || !treatment?.duration_mins) return slots;
    const slotMins = (new Date(slots[0].ends_at) - new Date(slots[0].starts_at)) / 60000;
    if (!slotMins || treatment.duration_mins <= slotMins) return slots;
    const needed = Math.ceil(treatment.duration_mins / slotMins);
    const startTimes = new Set(slots.map(s => new Date(s.starts_at).getTime()));
    const step = slotMins * 60000;
    return slots.filter(s => {
      const t0 = new Date(s.starts_at).getTime();
      for (let i = 1; i < needed; i++) { if (!startTimes.has(t0 + i * step)) return false; }
      return true;
    });
  })();

  return (
    <Elements stripe={stripePromise}>
      <div className="page">
        {/* Treatment hero photo */}
        {slug === 'inbody' ? (
          <div style={{
            position: 'relative', borderRadius: 16, overflow: 'hidden', marginBottom: 20,
            padding: '18px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #0F3D3E 0%, #155E63 100%)',
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#7FD1C4', letterSpacing: '0.14em', marginBottom: 4 }}>BODY COMPOSITION</div>
              <h1 style={{ color: '#fff', fontWeight: 800, fontSize: '1.3rem', marginBottom: 2 }}>{treatment.name}</h1>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>Self-service · {treatment.duration_mins} min</div>
            </div>
            <div style={{ marginLeft: 14, background: '#fff', borderRadius: 12, padding: 6, flexShrink: 0 }}>
              <img src="/images/inbody770.png" alt="InBody 770 scanner" style={{ display: 'block', height: 96, width: 'auto', objectFit: 'contain' }} />
            </div>
          </div>
        ) : TREATMENT_PHOTOS[slug] ? (
          <div style={{
            position: 'relative', borderRadius: 16, overflow: 'hidden', height: 180, marginBottom: 20,
            backgroundImage: `url(${TREATMENT_PHOTOS[slug]})`, backgroundSize: 'cover', backgroundPosition: 'center 35%',
          }}>
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.75) 100%)' }} />
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '16px 20px' }}>
              <h1 style={{ color: '#fff', fontWeight: 800, fontSize: '1.3rem', marginBottom: 2 }}>{treatment.name}</h1>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>{treatment.duration_mins} min session</div>
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: 20 }}>
            <h1 style={{ fontSize: '1.3rem', fontWeight: 800 }}>{treatment.name}</h1>
            <div style={{ color: 'var(--grey-500)', fontSize: '0.85rem' }}>{treatment.duration_mins} min session</div>
          </div>
        )}

        {/* Account credit banner */}
        {creditBalance > 0 && (
          <div style={{ background: 'var(--green-pale)', border: '1px solid #C8E6C9', borderRadius: 10, padding: '12px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '1.2rem' }}>💷</span>
            <div style={{ fontSize: '0.85rem', color: 'var(--grey-700)' }}>
              You have <strong style={{ color: 'var(--green)' }}>£{(creditBalance / 100).toFixed(2)} account credit</strong> — it'll be applied automatically at checkout.
            </div>
          </div>
        )}

        {/* Date picker */}
        <div className="section-title">Choose a Date</div>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8, marginBottom: 20, scrollbarWidth: 'none' }}>
          {dates.map(d => {
            const isSelected = d === date;
            const dateObj = new Date(d + 'T12:00:00');
            return (
              <button key={d} onClick={() => setDate(d)} style={{
                minWidth: 56, padding: '10px 8px', borderRadius: 10, textAlign: 'center',
                border: `2px solid ${isSelected ? 'var(--green)' : 'var(--grey-300)'}`,
                background: isSelected ? 'var(--green-pale)' : '#fff',
                cursor: 'pointer', flexShrink: 0,
              }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: isSelected ? 'var(--green)' : 'var(--grey-500)', textTransform: 'uppercase' }}>{format(dateObj, 'EEE')}</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: isSelected ? 'var(--green)' : 'var(--black)' }}>{format(dateObj, 'd')}</div>
                <div style={{ fontSize: '0.6rem', fontWeight: 700, color: isSelected ? 'var(--green)' : 'var(--grey-500)', textTransform: 'uppercase' }}>{format(dateObj, 'MMM')}</div>
              </button>
            );
          })}
        </div>

        {/* Slots */}
        <div className="section-title">Available Times</div>
        {slotsLoading ? <div className="loading-center"><div className="spinner" /></div> : (
          displaySlots.length === 0
            ? <div style={{ textAlign: 'center', color: 'var(--grey-500)', padding: 24 }}>No availability on this date. Try another day.</div>
            : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 24 }}>
                {displaySlots.map(s => {
                  const isSel = selectedSlot?.id === s.id;
                  const remaining = s.capacity - s.booked_count;
                  return (
                    <button key={s.id} onClick={() => setSelectedSlot(s)} style={{
                      padding: '12px 8px', borderRadius: 10, textAlign: 'center', fontWeight: 700,
                      border: `2px solid ${isSel ? 'var(--green)' : 'var(--grey-300)'}`,
                      background: isSel ? 'var(--green-pale)' : '#fff', cursor: 'pointer',
                    }}>
                      <div style={{ fontSize: '1rem', color: isSel ? 'var(--green)' : 'var(--black)' }}>
                        {format(new Date(s.starts_at), 'h:mm')}
                      </div>
                      <div style={{ fontSize: '0.65rem', color: 'var(--grey-500)', fontWeight: 400 }}>
                        {format(new Date(s.starts_at), 'aaa')}
                        {s.capacity > 1 && ` · ${remaining} left`}
                      </div>
                    </button>
                  );
                })}
              </div>
        )}

        {/* Booking form — guests are prompted to log in instead */}
        {selectedSlot && (
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ fontWeight: 700, marginBottom: 4 }}>Confirm Booking</h3>
            <p style={{ color: 'var(--grey-500)', fontSize: '0.85rem', marginBottom: 16 }}>
              {format(new Date(selectedSlot.starts_at), "EEEE d MMMM 'at' h:mm aaa")}
            </p>
            {session
              ? (slug === 'inbody'
                  ? <InBodyBookingForm treatment={treatment} slot={selectedSlot} onSuccess={() => setSuccess(true)} />
                  : <BookingForm treatment={treatment} slot={selectedSlot} wallet={wallet} creditBalance={creditBalance} onSuccess={() => setSuccess(true)} />)
              : <LoginToBook message="Log in or create a free account to confirm this booking." />}
          </div>
        )}
      </div>
    </Elements>
  );
}
